// The range picker's rules over a track (logic/track.ts): the track laid
// out as columns (`timelineOf`), and the handles, lanes, span, presets and
// URL of a range on it. Nothing here knows where the track came from; what
// differs by subject comes in as its facets (subject.ts).
import type { Facets } from '../subject';
import type { Track } from './track';

/** A column of the timeline: one point of the track, on the main lane or a replaced one. */
export interface RangePoint {
  /** URL value: `base`, or the full commit SHA. */
  value: string;
  sha: string;
  short: string;
  lane: 'main' | 'replaced';
  /**
   * Replaced lane: the column of the main point (or base) its run forks
   * off, or -1 when the track does not hold that commit. Null on the main
   * lane.
   */
  forksOff: number | null;
  /** First line of the commit message (the base point: the base's name). */
  message: string | null;
  author: string | null;
  at: string | null;
  /** Main lane: who force-pushed this head. Replaced lane: whose force-push replaced it. */
  forcePushedBy: string | null;
  /** A force-push precedes this main-lane point (the track breaks before it). */
  breakBefore: boolean;
  head: boolean;
  /** After the viewer's last submitted review. */
  isNew: boolean;
}

/**
 * A track laid out in columns, oldest first: replaced runs whose fork
 * point is not held first, then the base, then the main lane, each
 * replaced run right after the point it forks off.
 */
export interface RangeTimeline {
  points: RangePoint[];
  /** Point index of the viewer's last reviewed commit, or -1. */
  reviewed: number;
  /** Point index the "Last push" preset starts from, or -1. */
  lastPushFrom: number;
  /** Point index of the base. */
  base: number;
  /** Point index a range starts at when the URL names no `from` (the facets' `opens`), or -1. */
  opensFrom: number;
  /** The subject has reviews: "Since my last review" is offered. */
  reviews: boolean;
  /** The range opens on the latest commit (a branch history): "Latest commit" is offered. */
  latest: boolean;
  /** Older commits than the base exist and are not held. */
  hasOlder: boolean;
}

/** The columns of `track`, for a subject with `facets`. */
export function timelineOf(track: Track, facets: Pick<Facets, 'reviews' | 'opens'>): RangeTimeline {
  const points: RangePoint[] = [];
  const replaced = (runs: Track['main'][number]['replaced'], forksOff: number) => {
    for (const r of runs)
      points.push({ value: r.sha, sha: r.sha, short: r.short, lane: 'replaced', forksOff, message: r.message, author: r.author, at: r.at, forcePushedBy: r.replacedBy, breakBefore: false, head: false, isNew: false });
  };
  for (const run of track.unheld) replaced(run.replaced, -1);
  const b = track.base;
  const base = points.length;
  points.push({ value: 'base', sha: b.sha, short: b.sha.slice(0, 7), lane: 'main', forksOff: null, message: b.name, author: null, at: null, forcePushedBy: null, breakBefore: false, head: false, isNew: false });
  replaced(b.replaced, base);
  for (const p of track.main) {
    const at = points.length;
    points.push({ value: p.sha, sha: p.sha, short: p.short, lane: 'main', forksOff: null, message: p.message, author: p.author, at: p.at, forcePushedBy: p.forcePushedBy, breakBefore: p.breakBefore, head: p.sha === track.head, isNew: false });
    replaced(p.replaced, at);
  }
  const last = facets.reviews ? track.lastReview : null;
  const reviewed = last ? points.findIndex((p) => p.value === last) : -1;
  if (reviewed >= 0) for (let i = reviewed + 1; i < points.length; i++) points[i].isNew = true;
  const head = points.findIndex((p) => p.head);
  const latest = facets.opens === 'latest';
  return {
    points,
    reviewed,
    lastPushFrom: lastPushFrom(points, track.lastPush),
    base,
    opensFrom: latest ? previousMain(points, head) : base,
    reviews: facets.reviews,
    latest,
    hasOlder: track.hasOlder,
  };
}

/** The main-lane point before `i` (the base for the first commit), or -1. */
function previousMain(points: RangePoint[], i: number): number {
  for (let k = i - 1; k >= 0; k--) if (points[k].lane === 'main') return k;
  return -1;
}

/**
 * Where "Last push" starts: the track's previous head when it names one
 * (not the base), else the main-lane point before the head.
 */
function lastPushFrom(points: RangePoint[], before: string | null): number {
  const headIdx = points.findIndex((p) => p.head);
  if (headIdx <= 0) return -1;
  if (before) {
    const i = points.findIndex((p) => p.sha === before && p.value !== 'base');
    if (i >= 0) return i;
  }
  return previousMain(points, headIdx);
}

// ---------------------------------------------------------------- the range

/**
 * The two picked points: `from` (handle A, the diff's old side) and `to`
 * (handle B, the new side). Any two distinct points, on either lane, in
 * either order: the diff shows from → to as picked.
 */
export interface Range {
  from: number;
  to: number;
}
export type Handle = 'from' | 'to';

/**
 * Point index of a URL value (`from`/`to`), or -1 when it is not on the
 * timeline. `<sha>^` (a commit's parent, from a GitHub single-commit URL)
 * is the point before that commit on its lane.
 */
export function indexOf(points: RangePoint[], value: string): number {
  if (value.endsWith('^')) {
    const i = points.findIndex((p) => p.value === value.slice(0, -1));
    if (i < 0) return -1;
    for (let j = i - 1; j >= 0; j--) if (points[j].lane === points[i].lane) return j;
    return -1;
  }
  return points.findIndex((p) => p.value === value);
}

/**
 * The comparison the URL names, the one reading of it every view shares
 * (the diff model's mode, the timeline, Snapshots): `from` and `to` as
 * given; an absent `from` is where the range opens (the facets' `opens`),
 * null when that is the base (the diff is base → head); an absent `to` is
 * the head, null.
 */
export function comparisonOf(t: RangeTimeline, q: URLSearchParams): { from: string | null; to: string | null } {
  const opens = t.opensFrom === t.base ? null : (t.points[t.opensFrom]?.value ?? null);
  return { from: q.get('from') ?? opens, to: q.get('to') };
}

/**
 * The URL's `from`/`to` for two ends given by URL value, each null (left
 * out) where `comparisonOf` reads that end by default; off the default,
 * the base is written `from=base`.
 */
export function comparisonQuery(t: RangeTimeline, from: string, to: string): { from: string | null; to: string | null } {
  const opens = comparisonOf(t, new URLSearchParams()).from ?? 'base';
  const head = t.points.find((p) => p.head)?.value;
  return { from: from === opens ? null : from, to: to === head ? null : to };
}

/** The range the URL selects, as `comparisonOf` reads it: `from` absent = where the range opens, `to` absent = head. */
export function rangeFromQuery(t: RangeTimeline, q: URLSearchParams): { range: Range; from: string; to: string } {
  const { points } = t;
  const c = comparisonOf(t, q);
  const from = c.from ?? 'base';
  const to = c.to ?? points.find((p) => p.head)?.value ?? '';
  return { range: { from: indexOf(points, from), to: indexOf(points, to) }, from, to };
}

/** Query params for a range (`comparisonQuery` of its ends; an end off the timeline is left out). */
export function rangeToQuery(t: RangeTimeline, r: Range): { from: string | null; to: string | null } {
  const a = t.points[r.from];
  const b = t.points[r.to];
  const q = comparisonQuery(t, a?.value ?? '', b?.value ?? '');
  return { from: a ? q.from : null, to: b ? q.to : null };
}

/** A and B traded places. */
export function swap(r: Range): Range {
  return { from: r.to, to: r.from };
}

/** Put `handle` on point `i`; landing on the other handle's point changes nothing. */
export function setHandle(r: Range, handle: Handle, i: number): Range {
  const other = handle === 'from' ? r.to : r.from;
  if (i === other || i < 0) return r;
  return handle === 'from' ? { from: i, to: r.to } : { from: r.from, to: i };
}

/**
 * Keyboard move of a handle. ←/→ step to the previous or next point on the
 * handle's lane (stepping over the other handle), Home/End jump to the
 * lane's first or last free point, ↑/↓ switch to the replaced (above) or
 * main lane at the nearest column (ties go right, toward newer). Null for
 * other keys; the range unchanged when there is nowhere to go.
 */
export function keyMove(points: RangePoint[], r: Range, handle: Handle, key: string): Range | null {
  const at = handle === 'from' ? r.from : r.to;
  const other = handle === 'from' ? r.to : r.from;
  const here = points[at];
  if (!here) return ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key) ? r : null;
  const lane = here.lane;
  const free = (i: number, l: RangePoint['lane']) => i !== other && points[i].lane === l;
  let to = -1;
  if (key === 'ArrowLeft') {
    for (let i = at - 1; i >= 0 && to < 0; i--) if (free(i, lane)) to = i;
  } else if (key === 'ArrowRight') {
    for (let i = at + 1; i < points.length && to < 0; i++) if (free(i, lane)) to = i;
  } else if (key === 'Home') {
    for (let i = 0; i < points.length && to < 0; i++) if (free(i, lane)) to = i;
  } else if (key === 'End') {
    for (let i = points.length - 1; i >= 0 && to < 0; i--) if (free(i, lane)) to = i;
  } else if (key === 'ArrowUp' || key === 'ArrowDown') {
    const target: RangePoint['lane'] = key === 'ArrowUp' ? 'replaced' : 'main';
    if (target === lane) return r;
    let best = Infinity;
    points.forEach((_, i) => {
      if (!free(i, target)) return;
      const d = Math.abs(i - at);
      if (d < best || (d === best && i > at)) {
        best = d;
        to = i;
      }
    });
  } else return null;
  return to < 0 ? r : setHandle(r, handle, to);
}

/**
 * The track to point `i`, as drawn: the main-lane points up to it, or for
 * a replaced point, the main-lane points up to the one its run forks off,
 * then the run's points up to it (only the run, when its fork point is not
 * held).
 */
export function trackPath(points: RangePoint[], i: number): number[] {
  const p = points[i];
  if (!p) return [];
  let root = i;
  const run: number[] = [];
  if (p.lane === 'replaced') {
    for (let k = i; k >= 0 && points[k].lane === 'replaced' && points[k].forksOff === p.forksOff; k--) run.unshift(k);
    root = p.forksOff ?? -1;
  }
  const main: number[] = [];
  for (let k = 0; k <= root; k++) if (points[k].lane === 'main') main.push(k);
  return [...main, ...run];
}

export interface Span {
  /** Points the span covers, both ends included. */
  points: Set<number>;
  /** Consecutive point pairs to highlight along the track. */
  edges: [number, number][];
  /** One end lies on the other's track (no detour through a fork point). */
  oneLine: boolean;
  /** Steps along the track between the two ends. */
  apart: number;
}

/**
 * The stretch of track between A and B: from each end back to the last
 * point their tracks share. On one line that is the points between them;
 * across lanes it runs through the fork point.
 */
export function spanOf(points: RangePoint[], r: Range): Span | null {
  if (!points[r.from] || !points[r.to] || r.from === r.to) return null;
  const a = trackPath(points, r.from);
  const b = trackPath(points, r.to);
  let common = 0;
  while (common < a.length && common < b.length && a[common] === b[common]) common++;
  const set = new Set<number>();
  const edges: [number, number][] = [];
  // Tracks that share no point (a run whose fork point is not held) meet off the timeline: no edge joins them.
  for (const path of [a, b]) {
    for (let k = Math.max(common - 1, 0); k < path.length; k++) {
      set.add(path[k]);
      if (k >= Math.max(common, 1)) edges.push([path[k - 1], path[k]]);
    }
  }
  const oneLine = common === a.length || common === b.length;
  return { points: set, edges, oneLine, apart: a.length - common + (b.length - common) };
}

export interface Preset {
  id: 'all' | 'latest' | 'last-push' | 'since-review';
  label: string;
  range: Range | null;
  /** Why the preset is unavailable (shown as its tooltip). */
  disabled: string | null;
}

/**
 * The presets: All (base → head), Latest commit where the range opens on
 * it (a branch history), Last push, and Since my last review where the
 * subject has reviews.
 */
export function presets(t: RangeTimeline): Preset[] {
  const head = t.points.findIndex((p) => p.head);
  const all: Preset = {
    id: 'all',
    label: 'All',
    range: head > t.base ? { from: t.base, to: head } : null,
    disabled: head > t.base ? null : 'No commits yet',
  };
  const latest: Preset = {
    id: 'latest',
    label: 'Latest commit',
    range: t.opensFrom >= 0 && head > t.opensFrom ? { from: t.opensFrom, to: head } : null,
    disabled: t.opensFrom >= 0 && head > t.opensFrom ? null : 'No commits yet',
  };
  const last: Preset = {
    id: 'last-push',
    label: 'Last push',
    range: t.lastPushFrom >= 0 ? { from: t.lastPushFrom, to: head } : null,
    disabled: t.lastPushFrom >= 0 ? null : 'No earlier head to compare with',
  };
  let since: Preset;
  if (t.reviewed < 0) since = { id: 'since-review', label: 'Since my last review', range: null, disabled: 'You have not submitted a review on this pull request' };
  else if (t.reviewed === head) since = { id: 'since-review', label: 'Since my last review', range: null, disabled: 'Nothing new since your last review' };
  else since = { id: 'since-review', label: 'Since my last review', range: { from: t.reviewed, to: head }, disabled: null };
  return [all, ...(t.latest ? [latest] : []), last, ...(t.reviews ? [since] : [])];
}

/** Short label of a range end: `base`, `head`, or the short SHA. */
export function endLabel(p: RangePoint | undefined, value: string): string {
  if (!p) return value === 'base' ? 'base' : value.slice(0, 7);
  return p.value === 'base' ? 'base' : p.head ? 'head' : p.short;
}

/**
 * One-line summary: "A base → B head · 4 commits apart", or "· different
 * lanes" when neither end is on the other's track.
 */
export function rangeSummary(points: RangePoint[], r: Range, from: string, to: string): string {
  const text = `A ${endLabel(points[r.from], from)} → B ${endLabel(points[r.to], to)}`;
  const span = spanOf(points, r);
  if (!span) return text;
  if (!span.oneLine) return `${text} · different lanes`;
  return `${text} · ${span.apart} commit${span.apart === 1 ? '' : 's'} apart`;
}

/** A is later on the timeline than B: the diff runs backwards in time. */
export function reversed(r: Range): boolean {
  return r.from >= 0 && r.to >= 0 && r.from > r.to;
}

/** Tooltip text of a point. */
export function pointTitle(p: RangePoint): string {
  if (p.value === 'base') return `base · ${p.message ?? ''}${p.short ? ' @ ' + p.short : ''}`;
  const parts = [p.short];
  if (p.message) parts.push(p.message);
  if (p.author) parts.push(p.author);
  if (p.lane === 'replaced') parts.push(p.forcePushedBy ? `replaced by force-push by ${p.forcePushedBy}` : 'replaced by force-push');
  else if (p.forcePushedBy) parts.push(`force-pushed by ${p.forcePushedBy}`);
  if (p.head) parts.push('head');
  if (p.isNew) parts.push('new since your last review');
  return parts.join(' · ');
}
